import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";
import type { Conversation } from "@/messaging/types";
import { useChat } from "./ChatContext";
import { ConversationList, convTitle, counterpart } from "./ConversationList";
import { ConversationMenu } from "./ConversationMenu";
import { EmptyState } from "./EmptyState";
import { MembersPanel } from "./MembersPanel";
import { MessageThread } from "./MessageThread";
import { NewChat } from "./NewChat";
import {
  BackIcon,
  ChatIcon,
  CloseIcon,
  CollapseIcon,
  ExpandIcon,
  PlusIcon,
  UsersIcon,
} from "./icons";

type View = "list" | "new" | "thread" | "members";

export function ChatShell() {
  const { user } = useAuth();
  const myId = user?.id ?? null;
  const {
    open,
    expanded,
    activeId,
    conversations,
    listState,
    close,
    toggleExpanded,
    setActive,
    refreshList,
    upsertConversation,
    patchConversation,
    removeConversation,
    markRead,
    presenceOf,
  } = useChat();

  const [view, setView] = useState<View>("list");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const active = conversations.find((c) => c.id === activeId) ?? null;

  // Esc closes; scroll-lock while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  // Keep the view coherent with the active conversation.
  useEffect(() => {
    if (activeId && view === "list" && !expanded) setView("thread");
    if (!activeId && view === "thread") setView("list");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const selectConv = (id: string) => {
    setActive(id);
    setView("thread");
    markRead(id);
  };

  const backToList = () => {
    setView("list");
    if (!expanded) setActive(null);
  };

  const onCreated = (c: Conversation) => {
    upsertConversation(c);
    setActive(c.id);
    setView("thread");
    void refreshList();
  };

  const saveTitle = async () => {
    const t = titleDraft.trim();
    setEditingTitle(false);
    if (!active || !t || t === active.title) return;
    patchConversation(active.id, { title: t });
    try {
      await api(`/api/conversations/${active.id}`, { method: "PATCH", json: { title: t } });
    } catch {
      /* refresh reconciles */
    }
  };

  const listVisible = view === "list";
  // Only pin the list to a fixed width when it sits BESIDE the thread
  // (expanded two-pane). In the single-pane drawer it must fill the panel —
  // otherwise a 300px list leaves dead space on the right.
  const listPaneClass = expanded
    ? listVisible
      ? "flex w-full sm:w-[300px]"
      : "hidden sm:flex sm:w-[300px]"
    : listVisible
      ? "flex w-full"
      : "hidden";
  const rightPaneClass = expanded
    ? !listVisible
      ? "flex flex-1"
      : "hidden sm:flex sm:flex-1"
    : !listVisible
      ? "flex flex-1"
      : "hidden";

  return (
    <>
      {/* backdrop */}
      <div
        className={`fixed inset-0 z-[1099] bg-black/40 backdrop-blur-[2px] transition-opacity duration-200 ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={close}
        aria-hidden
      />
      {/* panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Chat"
        className={`fixed top-0 right-0 z-[1100] flex h-[100dvh] flex-col border-l border-white/10 bg-ink-900 shadow-2xl transition-transform duration-200 ease-out ${
          expanded ? "w-full sm:w-[720px] lg:w-[900px]" : "w-full sm:w-[440px] lg:w-[480px]"
        } ${open ? "translate-x-0" : "translate-x-full"}`}
      >
        {/* window control bar */}
        <div className="flex h-12 flex-shrink-0 items-center justify-between border-b border-white/5 px-3 safe-pt">
          <div className="flex items-center gap-2 text-slate-200">
            <ChatIcon size={18} />
            <span className="text-sm font-semibold">Messages</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={toggleExpanded}
              className="icon-btn hidden h-9 w-9 sm:inline-flex"
              title={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? <CollapseIcon /> : <ExpandIcon />}
            </button>
            <button onClick={close} className="icon-btn h-9 w-9" title="Close">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* panes */}
        <div className="flex min-h-0 flex-1">
          {/* left: list */}
          <div className={`${listPaneClass} min-h-0 flex-col border-r border-white/5`}>
            <div className="flex items-center justify-between px-3.5 py-2">
              <span className="card-title">Chats</span>
              <button
                onClick={() => setView("new")}
                className="btn-secondary !px-2.5 !py-1 !text-xs"
              >
                <PlusIcon size={14} /> New
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <ConversationList
                conversations={conversations}
                activeId={activeId}
                onSelect={selectConv}
                onNewChat={() => setView("new")}
                presenceOf={presenceOf}
                myId={myId}
                loading={listState === "loading"}
                error={listState === "error"}
                onRetry={() => void refreshList()}
              />
            </div>
          </div>

          {/* right: thread / new / members / empty */}
          <div className={`${rightPaneClass} min-h-0 flex-col`}>
            {view === "new" ? (
              <>
                <PaneHeader
                  title="New chat"
                  onBack={backToList}
                  showBack={!expanded}
                  expanded={expanded}
                />
                <div className="min-h-0 flex-1">
                  <NewChat onCreated={onCreated} presenceOf={presenceOf} />
                </div>
              </>
            ) : view === "members" && active ? (
              <>
                <PaneHeader
                  title="Members"
                  onBack={() => setView("thread")}
                  showBack
                  expanded={expanded}
                />
                <div className="min-h-0 flex-1">
                  <MembersPanel
                    conversation={active}
                    myId={myId}
                    presenceOf={presenceOf}
                    onChanged={() => void refreshList()}
                    onLeft={() => {
                      removeConversation(active.id);
                      setView("list");
                    }}
                  />
                </div>
              </>
            ) : active ? (
              <>
                {/* conversation header */}
                <div className="flex h-12 flex-shrink-0 items-center gap-2 border-b border-white/5 px-2">
                  {!expanded && (
                    <button onClick={backToList} className="icon-btn h-9 w-9" title="Back">
                      <BackIcon />
                    </button>
                  )}
                  <button
                    onClick={() => active.kind === "group" && setView("members")}
                    className="flex min-w-0 flex-1 items-center gap-2.5 rounded-lg px-1 py-1 text-left hover:bg-white/[0.03]"
                  >
                    {active.kind === "group" ? (
                      <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-accent-500/15 text-accent-300">
                        <UsersIcon size={17} />
                      </span>
                    ) : (
                      <Avatar
                        url={counterpart(active, myId)?.avatar_url}
                        name={counterpart(active, myId)?.full_name}
                        email={counterpart(active, myId)?.email}
                        size="sm"
                        status={presenceOf(counterpart(active, myId))}
                      />
                    )}
                    <div className="min-w-0">
                      {editingTitle && active.kind === "group" ? (
                        <input
                          autoFocus
                          value={titleDraft}
                          onChange={(e) => setTitleDraft(e.target.value)}
                          onBlur={saveTitle}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveTitle();
                            if (e.key === "Escape") setEditingTitle(false);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="input !py-1 !text-sm w-48"
                        />
                      ) : (
                        <div className="truncate text-sm font-semibold text-slate-100">
                          {convTitle(active, myId)}
                        </div>
                      )}
                      <div className="truncate text-[11px] text-slate-500">
                        {active.kind === "group"
                          ? `${active.members.length} members`
                          : presenceLabel(presenceOf(counterpart(active, myId)))}
                      </div>
                    </div>
                  </button>
                  <ConversationMenu
                    conversation={active}
                    myId={myId}
                    onPatch={patchConversation}
                    onViewMembers={() => setView("members")}
                    onRename={() => {
                      setTitleDraft(active.title || "");
                      setEditingTitle(true);
                    }}
                    onLeft={() => {
                      removeConversation(active.id);
                      setView("list");
                    }}
                  />
                </div>
                <MessageThread conversation={active} myId={myId} presenceOf={presenceOf} open={open} />
              </>
            ) : (
              <EmptyState
                icon={<ChatIcon />}
                title="Select a conversation"
                hint="Pick a chat on the left or start a new one."
              />
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

function PaneHeader({
  title,
  onBack,
  showBack,
  expanded,
}: {
  title: string;
  onBack: () => void;
  showBack: boolean;
  expanded: boolean;
}) {
  return (
    <div className="flex h-12 flex-shrink-0 items-center gap-2 border-b border-white/5 px-2">
      {showBack && (
        <button
          onClick={onBack}
          className={`icon-btn h-9 w-9 ${expanded ? "sm:hidden" : ""}`}
          title="Back"
        >
          <BackIcon />
        </button>
      )}
      <span className="px-1 text-sm font-semibold text-slate-100">{title}</span>
    </div>
  );
}

function presenceLabel(status: string): string {
  if (status === "active") return "Active now";
  if (status === "away") return "Away";
  return "Offline";
}
